import React, { Fragment, useEffect } from "react"
import Draggable from "react-draggable"
import { ResizableBox } from "react-resizable"

import CloseIcon from "@mui/icons-material/Close"
import {
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Dialog as MuiDialog,
  Paper,
} from "@mui/material"
import T from "prop-types"
import "react-resizable/css/styles.css"

import { useDynamicPaperStyles, useOverrides } from "./css"
import { Button } from "../../molecules"

function DraggablePaper(props) {
  return (
    <Draggable
      handle="#draggable-dialog-title"
      bounds={"body"}
      cancel={'[class*="MuiDialogContent-root"]'}>
      <Paper {...props} />
    </Draggable>
  )
}

//TODO: Reduce number of props
export const Dialog = ({
  actionButtons,
  children,
  className,
  confirmDisabled,
  content,
  declineText = "Cancel",
  disablePortal,
  draggable = false,
  fullWidth = false,
  hideBackdrop = false,
  loading = false,
  maxHeight = "calc(100% - 64px)",
  maxWidth = false,
  maxWidthValue = "",
  noPadding = false,
  onBackdropClick,
  onClose,
  onConfirm,
  onDecline,
  open = false,
  overflow = false,
  renderButtons = true,
  resizable = false,
  resizableHeight = 600,
  resizableWidth = 1000,
  showClose = false,
  submitText = "Confirm",
  title = null,
  width = false,
  ...props
}) => {
  const {
    closeIcon,
    dialogActions,
    dialogContent,
    dialogTitle,
    loadingSpinnerWrapper,
    paper,
    root,
    spacing,
  } = useOverrides({
    maxHeight,
    maxWidthValue,
    noPadding,
    overflow,
    hideButtons: !renderButtons,
    resizable,
    title,
    width,
  })

  const defaultActions = (
    <Fragment>
      <Button
        variant="contained"
        color="primary"
        label={submitText}
        onClick={onConfirm}
        disabled={confirmDisabled}
      />

      <Button color="secondary" variant="outlined" label={declineText} onClick={onDecline} />
    </Fragment>
  )

  useEffect(() => {
    const close = (e) => {
      if (!(e.keyCode === 27)) return
      if (!onClose) return
      onClose()
    }

    window.addEventListener("keydown", close)
    return () => window.removeEventListener("keydown", close)
  }, [])

  const dialogElements = (
    <>
      <DialogTitle
        classes={{ root: dialogTitle }}
        style={{ cursor: draggable ? "move" : "default" }}
        id="draggable-dialog-title">
        {title}
        {((onClose && !renderButtons) || showClose) && (
          <IconButton classes={{ root: closeIcon }} aria-label="Close" onClick={onClose}>
            <CloseIcon />
          </IconButton>
        )}
      </DialogTitle>

      <DialogContent classes={{ root: dialogContent }}>
        {content || children}
        {loading && <div className={loadingSpinnerWrapper}></div>}
      </DialogContent>

      {renderButtons && (
        <DialogActions classes={{ root: dialogActions, spacing }}>
          {actionButtons || defaultActions}
        </DialogActions>
      )}
    </>
  )

  const getMaxConstraint = (value) => value - 70

  return (
    <MuiDialog
      {...props}
      classes={{ paper, root }}
      PaperComponent={draggable ? DraggablePaper : Paper}
      PaperProps={{
        className,
        classes: useDynamicPaperStyles({ maxHeight, noPadding, overflow, renderButtons, width }),
      }}
      aria-labelledby="draggable-dialog-title"
      {...{ disablePortal, fullWidth, maxWidth, onBackdropClick, onClose, open, hideBackdrop }}>
      {resizable ? (
        <ResizableBox
          width={resizableWidth}
          height={resizableHeight}
          minConstraints={[720, 10]}
          maxConstraints={[
            getMaxConstraint(window.innerWidth),
            getMaxConstraint(window.innerHeight),
          ]}>
          {dialogElements}
        </ResizableBox>
      ) : (
        dialogElements
      )}
    </MuiDialog>
  )
}

Dialog.propTypes = {
  actionButtons: T.oneOfType([T.element, T.node]),
  className: T.any,
  content: T.oneOfType([T.element, T.node]),
  declineText: T.string,
  disablePortal: T.bool,
  fullWidth: T.bool,
  maxHeight: T.string,
  onBackdropClick: T.func,
  onConfirm: T.func,
  onDecline: T.func,
  open: T.bool,
  overflow: T.bool,
  submitText: T.string,
  title: T.string,
  showClose: T.bool,
}
